import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { brmAPI, demoAPI } from "@/services/api";
import { setCachedEnrollments, getCachedEnrollments } from "@/services/offline";
import { Search, RefreshCw, CheckCircle2, Clock, XCircle, Users, Zap } from "lucide-react";
import {
  TailAdminEmptyState,
  TailAdminStatusBadge,
} from "@/components/template/tailadmin/DashboardPrimitives";

const BRMMyUsers = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [activating, setActivating] = useState(null);

  const fetchEnrollments = async (status = null) => {
    setLoading(true);
    try {
      const res = await brmAPI.getEnrollments(status === "all" ? null : status);
      setEnrollments(res.data);
      setCachedEnrollments(res.data);
    } catch (error) {
      const cached = getCachedEnrollments();
      if (cached) {
        setEnrollments(cached.data);
        toast.info("Showing cached data");
      } else {
        toast.error("Failed to load enrollments");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnrollments(activeTab);
  }, [activeTab]);

  const handleActivate = async (enrollmentId) => {
    setActivating(enrollmentId);
    try {
      const res = await demoAPI.activateUser(enrollmentId);
      toast.success(`User activated! Commission: ₦${res.data.commission_earned.toLocaleString()}`);
      fetchEnrollments(activeTab);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to activate");
    } finally {
      setActivating(null);
    }
  };

  const filteredEnrollments = enrollments.filter((e) =>
    e.phone_masked.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return (
          <TailAdminStatusBadge tone="success">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Active
          </TailAdminStatusBadge>
        );
      case "pending":
        return (
          <TailAdminStatusBadge tone="warning">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </TailAdminStatusBadge>
        );
      case "churned":
        return (
          <TailAdminStatusBadge tone="danger">
            <XCircle className="w-3 h-3 mr-1" />
            Churned
          </TailAdminStatusBadge>
        );
      default:
        return <TailAdminStatusBadge tone="neutral">{status}</TailAdminStatusBadge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-NG", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  const counts = {
    all: enrollments.length,
    active: enrollments.filter((e) => e.status === "active").length,
    pending: enrollments.filter((e) => e.status === "pending").length,
    churned: enrollments.filter((e) => e.status === "churned").length
  };

  return (
    <div className="px-4 py-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-gray-900" data-testid="my-users-title">
            My Users
          </h1>
          <p className="text-sm text-gray-500">
            {enrollments.length} total enrollment{enrollments.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchEnrollments(activeTab)}
          disabled={loading}
          data-testid="refresh-users-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <Input
          placeholder="Search by phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-11 border-gray-200 bg-white pl-10 focus:border-brand-400"
          data-testid="search-users-input"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full grid grid-cols-4">
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          <TabsTrigger value="active" data-testid="tab-active">Active</TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="churned" data-testid="tab-churned">Churned</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4 space-y-3">
          {loading ? (
            // Loading skeletons
            Array.from({ length: 5 }).map((_, i) => (
              <section key={i} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                    <Skeleton className="h-6 w-16" />
                  </div>
              </section>
            ))
          ) : filteredEnrollments.length === 0 ? (
            <TailAdminEmptyState
              icon={Users}
              title={search ? "No users match your search" : "No enrollments yet"}
              description={search ? "Try another phone number." : "New enrollments will appear here."}
            />
          ) : (
            filteredEnrollments.map((enrollment) => (
              <section key={enrollment.id} data-testid={`enrollment-${enrollment.id}`} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 tabular-nums">
                        {enrollment.phone_masked}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Enrolled: {formatDate(enrollment.enrolled_at)}
                      </p>
                      {enrollment.subscribed_at && (
                        <p className="text-xs text-emerald-600 mt-1">
                          Subscribed: {formatDate(enrollment.subscribed_at)}
                        </p>
                      )}
                      {enrollment.commission_paid > 0 && (
                        <p className="text-sm font-semibold text-brand-500 mt-1">
                          +₦{enrollment.commission_paid.toLocaleString()}
                        </p>
                      )}
                    </div>
                    <div className="text-right space-y-2">
                      {getStatusBadge(enrollment.status)}
                      {enrollment.status === "pending" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs h-7"
                          onClick={() => handleActivate(enrollment.id)}
                          disabled={activating === enrollment.id}
                          data-testid={`activate-${enrollment.id}`}
                        >
                          {activating === enrollment.id ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : (
                            <>
                              <Zap className="w-3 h-3 mr-1" />
                              Demo Activate
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
              </section>
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BRMMyUsers;
